import { notification } from "antd";
import cuid from "cuid";
import { filter, get, isArray, isNil, last, sortBy } from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import format from "string-template";
import { GENERATE_QUOTE_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { BUILD_GENERAL_COSTS } from "utils/api/graphql/mutations/costs";
import { CREATE_LINK } from "utils/api/graphql/mutations/link";
import { UPDATE_PROJECT } from "utils/api/graphql/mutations/projects";
import { SEND_B2C_QUOTE } from "utils/api/graphql/mutations/quotes";
import { CREATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { GENERAL_COSTS } from "utils/api/graphql/queries/general-cost";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import { OFFER } from "utils/api/graphql/queries/offers";
import {
  DATE_FORMAT,
  INSURANCE_PLAN_PARAMS_RULES,
  KEYS,
} from "utils/constants";
import { getMinDeltaStart } from "utils/helpers/array";
import { generateSQLCondition } from "utils/helpers/graphql";
import { filterByConditions } from "../../../..";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

const CDS = ({ processId, tracer, project, onNext, onBack, currentStatus }) => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [buildGeneralCosts] = useMutation(BUILD_GENERAL_COSTS);
  const [updateProject] = useMutation(UPDATE_PROJECT);
  const [addSubscription] = useMutation(CREATE_SUBSCRIPTION);
  const [generalCosts] = useLazyQuery(GENERAL_COSTS);
  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS);
  const [sendQuoteMutation] = useMutation(SEND_B2C_QUOTE);
  const [addLink] = useMutation(CREATE_LINK);
  const [selectedValue, setSelectedValue] = useState({
    ...get(project, "contact.fields", {}),
    isActive: get(project, "fields.info.offerCDS"),
  });
  const [getOffer] = useLazyQuery(OFFER, {
    fetchPolicy: "no-cache",
  });
  const [getDeltaStart] = useLazyQuery(INSURANCE_PLANS, {
    fetchPolicy: "no-cache",
    variables: {
      where: {
        isStandalone: true,
      },
    },
  });
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("DETAIL", { returnObjects: true });

  const onSubmit = async (insurancePlanId) => {
    const { data } = await getOffer({
      variables: {
        where: {
          AND: [
            { offerInsurancePlans: { insurancePlan: { id: insurancePlanId } } },
          ],
        },
      },
    });
    const mainOfferId = get(data, "offer.id", null);
    const { data: offer } = await getOffer({
      fetchPolicy: "no-cache",
      variables: {
        where: {
          AND: [{ id: mainOfferId }, { offerInsurancePlans: { main: false } }],
        },
      },
    });
    const hasCDS = !!get(offer, "offer.offerInsurancePlans", []).filter(
      ({ weight }) => weight === 2,
    )?.length;

    const insurancePlans = [
      ...get(project, "fields.insurancePlans", []).filter(
        (id) => id !== insurancePlanId,
      ),
      insurancePlanId,
    ];
    if (hasCDS)
      return onNext({
        payload: {
          fields: {
            ...project.fields,
            selectedToCompare: insurancePlans,
            lastSelected: [insurancePlanId],
            hasCDS,
            info: {
              ...get(project, "fields.info", {}),
              offerCDS: selectedValue?.isActive,
            },
          },
        },
      });
    const status = get(
      last(
        sortBy(
          filter(
            project.statuses,
            ({ status }) => status.process.id === processId,
          ),
          "createdDate",
        ),
      ),
      "status",
    );
    const { possibleStatuses } = status;
    const [possibleStatus] = filterByConditions(
      sortBy(possibleStatuses, "order"),
      {
        ...project,
        status,
      },
    );

    addSubscription({
      variables: {
        data: {
          id: cuid(),
          project: { id: project.id },
          insurancePlan: {
            id: insurancePlanId,
          },
        },
      },
      onCompleted: ({
        addSubscription: { id: subscriptionId, insurancePlan },
      }) => {
        onNext({
          payload: {
            locked: true,
            visible: true,
            fields: {
              ...project.fields,
              insurancePlans,
              selectedToCompare: insurancePlans,
              lastSelected: [insurancePlanId],
              ...(!isNil(possibleStatus?.nextStatus) && {
                nextQuoteStatus: possibleStatus.nextStatus,
              }),
              currentSubscription: subscriptionId,
              hasCDS,
            },
          },
          onCompleted: () => {
            generateQuoteDocuments({
              variables: {
                data: {
                  project: { id: project.id },
                  insurancePlans: [{ id: insurancePlanId }],
                },
              },
              onCompleted: () => {
                const source = localStorage.getItem(KEYS.SOURCE);
                addLink({
                  variables: {
                    data: {
                      url: get(
                        insurancePlan,
                        "config.generatedUrl",
                        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
                      ),
                      project: { id: project.id },
                      subscription: { id: subscriptionId },
                      fields: {
                        process: { id: processId },
                        status: { id: get(possibleStatus, "nextStatus") },
                      },
                    },
                  },
                  onCompleted: ({ addLink }) => {
                    sendQuoteMutation({
                      variables: {
                        data: {
                          link: addLink?.id,
                          project: { id: project.id },
                          insurancePlans: [{ id: insurancePlanId }],
                        },
                      },
                      onCompleted: () =>
                        notification.open({
                          message: "Devis envoyé",
                          duration: 5,
                          icon: (
                            <IoCheckmarkCircleOutline
                              color={COLORS.C_SUCCESS}
                            />
                          ),
                        }),
                    });
                  },
                });
              },
            });
          },
        });
      },
    });
  };

  useEffect(() => {
    getDeltaStart({
      onCompleted: ({ insurancePlans: { data: insurancePlans } }) => {
        const deltaRia = getMinDeltaStart({ insurancePlans, minValue: 30 });
        const deltaStart = getMinDeltaStart({
          insurancePlans,
          minValue: 1,
          key: INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
        });
        const deltaS = project.ria ? deltaRia : deltaStart;
        if (project?.expired) {
          notification.open({
            message: format(TRANSLATION.EXPIRATION.TITLE, {
              issuanceDate: moment().add(deltaS, "days").format(DATE_FORMAT),
            }),
            description: TRANSLATION.EXPIRATION.DESCRIPTION,
            duration: 5,
            icon: <HiOutlineExclamationCircle size={32} />,
            type: "error",
          });
        }
        const issuanceDate = moment().add(deltaS, "days");

        updateProject({
          variables: {
            where: { id: project.id },
            data: {
              expired: false,
              contract: {
                ...project.contract,
                issuanceDate,
              },
            },
          },
          onCompleted: () => {
            const profession = get(project, "contact.profession");

            const sqlConditions = [
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.PROFESSION,
                `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
                `'${profession}'`,
              ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.PROCESSES,
                `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
                `'${processId}'`,
              ),
            ];

            const insurancePlanFilter = {
              where: {
                isStandalone: true,
              },
              rules: sqlConditions.filter(Boolean),
              custom: {
                AND: [
                  {
                    OR: [
                      { startDate: null },
                      {
                        startDate: issuanceDate,
                        operation: "<=",
                      },
                    ],
                  },
                  {
                    OR: [
                      { endDate: null },
                      {
                        endDate: issuanceDate,
                        operation: ">=",
                      },
                    ],
                  },
                ],
              },
            };
            buildGeneralCosts({
              variables: {
                data: {
                  project: { id: project.id },
                  insurancePlanFilter,
                },
              },
              onCompleted: ({ buildGeneralCosts }) => {
                const ids = buildGeneralCosts
                  .filter(({ cost }) => cost)
                  .map(({ id }) => id);
                generalCosts({
                  fetchPolicy: "no-cache",
                  variables: {
                    isIn: { id: ids },
                  },
                  onCompleted: ({ generalCosts }) => {
                    const productsData = omitDeep(
                      get(generalCosts, "data", []),
                      "__typename",
                    );

                    const recommendedIndex = productsData.findIndex(
                      (item) => item.recommended,
                    );
                    if (recommendedIndex !== -1) {
                      const [recommendedProduct] = productsData.splice(
                        recommendedIndex,
                        1,
                      );
                      productsData.splice(1, 0, recommendedProduct);
                    }

                    setProducts(productsData);
                    setLoading(false);
                  },
                });
              },
            });
          },
        });
      },
    });
  }, []);

  if (loading || !isArray(products)) return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        progress={currentStatus?.progress}
        tracer={tracer}
      />
      <View
        product={products[0]}
        onBack={onBack}
        onSubmit={onSubmit}
        locked={project.locked}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
      />
    </>
  );
};

export default CDS;
